/**
 * JWT Utilities for iBill Launchpad
 *
 * Helper functions to parse and extract data from Keycloak JWT tokens
 */

/**
 * Parse JWT token and return decoded payload
 *
 * @param {string} token - JWT access token
 * @returns {Object|null} Decoded token payload or null if invalid
 */
export const parseJwt = (token) => {
  try {
    if (!token) return null;

    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing JWT token:', e);
    return null;
  }
};

/**
 * Extract permissions from Keycloak JWT token
 *
 * Permissions are located at: resource_access.ibill.roles
 *
 * @param {string} token - JWT access token
 * @returns {string[]} Array of permission strings
 *
 * @example
 * const permissions = extractPermissionsFromToken(accessToken);
 * // Returns: ['ibill:zone:view', 'ibill:zone:add', 'ibill:zone:edit', ...]
 */
export const extractPermissionsFromToken = (token) => {
  const decoded = parseJwt(token);

  if (!decoded) {
    console.warn('Unable to decode JWT token');
    return [];
  }

  try {
    // Extract realm roles
    const realmRoles = Array.isArray(decoded?.realm_access?.roles) 
      ? decoded.realm_access.roles 
      : [];

    // Extract resource roles from all resource_access entries (ibill, account, etc.)
    const resourceRoles = Object.values(decoded?.resource_access || {})
      .flatMap(resource => (Array.isArray(resource.roles) ? resource.roles : []));

    // Combine all roles without duplicates
    const allPermissions = [...new Set([...realmRoles, ...resourceRoles])];

    return allPermissions;
  } catch (e) {
    console.error('Error extracting permissions from token:', e);
    return [];
  }
};

/**
 * Determine whether a token grants access to ibill-launchpad.
 *
 * Access is gated specifically on the launchpad client's role block
 * (resource_access[<client>].roles), NOT the merged permission list from
 * extractPermissionsFromToken - that one also includes 'account' and realm
 * roles, so a partner-console user (same realm, no launchpad roles) would
 * falsely pass. The client id comes from REACT_APP_OAUTH_CLIENT_ID so this
 * stays correct across environments.
 *
 * @param {string} token - JWT access token
 * @returns {boolean} true if the token has at least one launchpad client role
 */
export const hasLaunchpadAccess = (token) => {
  try {
    const decoded = parseJwt(token);
    const client = process.env.REACT_APP_OAUTH_CLIENT_ID;
    const roles = decoded?.resource_access?.[client]?.roles;
    return Array.isArray(roles) && roles.length > 0;
  } catch (e) {
    console.error('Error checking launchpad access:', e);
    return false;
  }
};

/**
 * Extract user information from JWT token
 *
 * @param {string} token - JWT access token
 * @returns {Object} User information object
 *
 * @example
 * const user = extractUserInfoFromToken(accessToken);
 * // Returns: { username: 'admin-abspl', name: 'Admin ABSPL', email: '...', ... }
 */
export const extractUserInfoFromToken = (token) => {
  const decoded = parseJwt(token);

  if (!decoded) {
    return null;
  }

  return {
    username: decoded.preferred_username,
    name: decoded.name,
    firstName: decoded.given_name,
    lastName: decoded.family_name,
    email: decoded.email,
    emailVerified: decoded.email_verified,
    tenant: decoded.tenant,
    company: decoded.tenant?.company,
    sub: decoded.sub,
    iss: decoded.iss,
    exp: decoded.exp,
    iat: decoded.iat,
  };
};

/**
 * Check if JWT token is expired
 *
 * @param {string} token - JWT access token
 * @returns {boolean} True if expired, false otherwise
 */
export const isTokenExpired = (token) => {
  const decoded = parseJwt(token);

  if (!decoded || !decoded.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

/**
 * Get token expiration time
 *
 * @param {string} token - JWT access token
 * @returns {Date|null} Expiration date or null if invalid
 */
export const getTokenExpiration = (token) => {
  const decoded = parseJwt(token);

  if (!decoded || !decoded.exp) {
    return null;
  }

  // Convert from seconds to milliseconds
  return new Date(decoded.exp * 1000);
};

/**
 * Get time until token expiration in seconds
 *
 * @param {string} token - JWT access token
 * @returns {number} Seconds until expiration, or 0 if expired
 */
export const getTimeUntilExpiration = (token) => {
  const decoded = parseJwt(token);

  if (!decoded || !decoded.exp) {
    return 0;
  }

  const currentTime = Date.now() / 1000;
  const timeRemaining = decoded.exp - currentTime;

  return Math.max(0, Math.floor(timeRemaining));
};

/**
 * Extract realm roles from token
 *
 * @param {string} token - JWT access token
 * @returns {string[]} Array of realm roles
 */
export const extractRealmRoles = (token) => {
  const decoded = parseJwt(token);

  if (!decoded) {
    return [];
  }

  return decoded?.realm_access?.roles || [];
};

/**
 * Check if user has a specific role
 *
 * @param {string} token - JWT access token
 * @param {string} role - Role to check
 * @returns {boolean} True if user has the role
 */
export const hasRole = (token, role) => {
  const permissions = extractPermissionsFromToken(token);
  return permissions.includes(role);
};

/**
 * Check if current user has a specific permission
 * Reads token from localStorage and checks for permission
 *
 * @param {string} permission - Permission to check (e.g., 'cfg:idn-usr:v')
 * @returns {boolean} True if user has the permission
 */
export const canAccess = (permission) => {
  try {
    const token = localStorage.getItem('ibill_access_token');
    if (!token) {
      console.warn('canAccess: No token found in localStorage');
      return false;
    }
    
    const permissions = extractPermissionsFromToken(token);
    console.log('canAccess: Checking permission:', permission);
    console.log('canAccess: User has permissions:', permissions);
    console.log('canAccess: Result:', permissions.includes(permission));
    
    return permissions.includes(permission);
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

/**
 * Normalize permissions to domain-driven format
 *
 * Permissions in JWT are already in DOMAIN:SUBDOMAIN:ACTION format
 * This function ensures consistency and handles any legacy formats
 *
 * @param {string[]} permissions - Array of permission strings from JWT
 * @returns {string[]} Array of normalized permission strings
 */
export const normalizePermissions = (permissions) => {
  // Permissions should already be in correct format: ZONE:MANAGEMENT:VIEW
  // Just return them as-is, ensuring uppercase
  return permissions.map(p => p.toUpperCase());
};

/**
 * Map old permission format to new domain-driven format (if needed)
 *
 * Only use this if migrating from old permission system
 *
 * @param {string[]} oldPermissions - Array of old format permissions
 * @returns {string[]} Array of new format permissions
 */
export const mapLegacyPermissions = (keycloakPermissions) => {
  const permissionMap = {
    // Zone Management
    'ibill:zone:view': 'ZONE_LIST_VIEW',
    'ibill:zone:add': 'ZONE_CREATE',
    'ibill:zone:edit': 'ZONE_UPDATE',
    'ibill:zone:delete': 'ZONE_DELETE',

    // Company Management
    'ibill:company:view': 'COMPANY_LIST_VIEW',
    'ibill:company:add': 'COMPANY_CREATE',
    'ibill:company:edit': 'COMPANY_UPDATE',
    'ibill:company:delete': 'COMPANY_DELETE',

    // Server Management
    'ibill:server:view': 'SERVER_LIST_VIEW',
    'ibill:server:add': 'SERVER_CREATE',
    'ibill:server:edit': 'SERVER_UPDATE',
    'ibill:server:delete': 'SERVER_DELETE',
    'ibill:server:management': 'SERVER_SYNC_MASTER_DATA',

    // Package Management
    'ibill:package:view': 'PACKAGE_LIST_VIEW',
    'ibill:package:add': 'PACKAGE_CREATE',
    'ibill:package:edit': 'PACKAGE_UPDATE',
    'ibill:package:delete': 'PACKAGE_DELETE',

    // Distributor Management
    'ibill:distributor:view': 'DISTRIBUTOR_LIST_VIEW',
    'ibill:distributor:add': 'DISTRIBUTOR_CREATE',
    'ibill:distributor:edit': 'DISTRIBUTOR_UPDATE',
    'ibill:distributor:delete': 'DISTRIBUTOR_DELETE',

    // Legal Office
    'ibill:legaloffice:view': 'LEGAL_OFFICE_LIST_VIEW',
    'ibill:legaloffice:add': 'LEGAL_OFFICE_CREATE',
    'ibill:legaloffice:edit': 'LEGAL_OFFICE_UPDATE',
    'ibill:legaloffice:delete': 'LEGAL_OFFICE_DELETE',

    // Support Office
    'ibill:supportoffice:view': 'SUPPORT_OFFICE_LIST_VIEW',
    'ibill:supportoffice:add': 'SUPPORT_OFFICE_CREATE',
    'ibill:supportoffice:edit': 'SUPPORT_OFFICE_UPDATE',
    'ibill:supportoffice:delete': 'SUPPORT_OFFICE_DELETE',

    // Commission
    'ibill:commission:rule:view': 'COMMISSION_RULE_LIST_VIEW',
    'ibill:commission:rule:add': 'COMMISSION_RULE_CREATE',
    'ibill:commission:rule:edit': 'COMMISSION_RULE_UPDATE',
    'ibill:commission:rule:delete': 'COMMISSION_RULE_DELETE',
    'ibill:commission:slab:view': 'COMMISSION_SLAB_LIST_VIEW',
    'ibill:commission:slab:add': 'COMMISSION_SLAB_CREATE',
    'ibill:commission:slab:edit': 'COMMISSION_SLAB_UPDATE',
    'ibill:commission:slab:delete': 'COMMISSION_SLAB_DELETE',

    // OTT
    'ott:bundle:list': 'OTT_BUNDLE_LIST_VIEW',
    'ott:bundle:add': 'OTT_BUNDLE_CREATE',
    'ott:bundle:edit': 'OTT_BUNDLE_UPDATE',
    'ott:bundle:delete': 'OTT_BUNDLE_DELETE',
    'ott:bundle:configure': 'OTT_BUNDLE_CONFIGURE',
    'ott:ott:list': 'OTT_DIGITAL_SERVICE_LIST_VIEW',
    'ott:ott:add': 'OTT_DIGITAL_SERVICE_CREATE',
    'ott:ott:edit': 'OTT_DIGITAL_SERVICE_UPDATE',
    'ott:ott:delete': 'OTT_DIGITAL_SERVICE_DELETE',
    'ott:pack:add': 'OTT_VAS_PROVIDER_PACKAGE_CREATE',
    'ott:source:list': 'OTT_SOURCE_CONFIG_LIST_VIEW',
    'ott:source-detail:add': 'OTT_SOURCE_CONFIG_CREATE',
    'ott:source-detail:edit': 'OTT_SOURCE_CONFIG_UPDATE',
    'ott:eligibility:list': 'OTT_AUTO_ELIGIBILITY_LIST_VIEW',
    'ott:eligibility:admin': 'OTT_AUTO_ELIGIBILITY_CREATE',
    'ott:coupons:list': 'OTT_COUPON_LIST_VIEW',
    'ott:coupons:upload': 'OTT_COUPON_UPLOAD',
    'ott:manual:console': 'OTT_MANUAL_CONSOLE_VIEW',

    // CAF Module
    'ibill:caf:module': 'CAF_MODULE_ACCESS',
    'caf:approver:get': 'CAF_APPROVER_VIEW',

    // Docket Module
    'docket:view:list': 'DOCKET_LIST_VIEW',
    'docket:create:new': 'DOCKET_CREATE',
    'docket:broadband:create:new': 'DOCKET_BROADBAND_CREATE',
    'docket:create:new:internal': 'DOCKET_INTERNAL_CREATE',
    'docket:add:comment': 'DOCKET_COMMENT_ADD',
    'docket:add:customer:comment': 'DOCKET_CUSTOMER_COMMENT_ADD',
    'docket:add:attachment': 'DOCKET_ATTACHMENT_ADD',
    'docket:car:list': 'DOCKET_CAR_LIST_VIEW',
    'docket:car:add': 'DOCKET_CAR_CREATE',
    'docket:car:edit': 'DOCKET_CAR_UPDATE',
    'docket:department:list': 'DOCKET_DEPARTMENT_LIST_VIEW',
    'docket:department:add': 'DOCKET_DEPARTMENT_CREATE',
    'docket:department:edit': 'DOCKET_DEPARTMENT_UPDATE',
    'docket:designation:list': 'DOCKET_DESIGNATION_LIST_VIEW',
    'docket:designation:add': 'DOCKET_DESIGNATION_CREATE',
    'docket:designation:edit': 'DOCKET_DESIGNATION_UPDATE',
    'docket:employee:list': 'DOCKET_EMPLOYEE_LIST_VIEW',
    'docket:employee:add': 'DOCKET_EMPLOYEE_CREATE',
    'docket:employee:edit': 'DOCKET_EMPLOYEE_UPDATE',
    'docket:office:list': 'DOCKET_OFFICE_LIST_VIEW',
    'docket:office:add': 'DOCKET_OFFICE_CREATE',
    'docket:office:edit': 'DOCKET_OFFICE_UPDATE',
    'docket:team:list': 'DOCKET_TEAM_LIST_VIEW',
    'docket:team:add': 'DOCKET_TEAM_CREATE',
    'docket:team:edit': 'DOCKET_TEAM_UPDATE',

    // Role-based permissions
    'COMPANY_ADMIN': 'COMPANY_ADMIN_ROLE',
    'OTT_ADMIN_ROLE': 'OTT_ADMIN_ROLE',
    'DOCKET_ADMIN': 'DOCKET_ADMIN_ROLE',
    'CAF Approver': 'CAF_APPROVER_ROLE',
  };

  // Map Keycloak permissions to LaunchPad format
  const mappedPermissions = keycloakPermissions.map(
    permission => permissionMap[permission] || permission
  );

  // Also include original Keycloak permissions for backward compatibility
  return [...new Set([...mappedPermissions, ...keycloakPermissions])];
};

/**
 * Debug function to log token information
 *
 * @param {string} token - JWT access token
 */
export const debugToken = (token) => {
  const decoded = parseJwt(token);

  if (!decoded) {
    console.log('Invalid token');
    return;
  }

  console.log('=== JWT Token Debug Info ===');
  console.log('User:', decoded.preferred_username);
  console.log('Name:', decoded.name);
  console.log('Email:', decoded.email);
  console.log('Company:', decoded.tenant?.company);
  console.log('Issued At:', new Date(decoded.iat * 1000).toLocaleString());
  console.log('Expires At:', new Date(decoded.exp * 1000).toLocaleString());
  console.log('Is Expired:', isTokenExpired(token));
  console.log('Time Until Expiration:', getTimeUntilExpiration(token), 'seconds');
  console.log('Realm Roles:', decoded.realm_access?.roles || []);
  console.log('iBill Roles (Permissions):', decoded.resource_access?.ibill?.roles || []);
  console.log('Total Permissions:', (decoded.resource_access?.ibill?.roles || []).length);
  console.log('===========================');
};

export default {
  parseJwt,
  extractPermissionsFromToken,
  extractUserInfoFromToken,
  isTokenExpired,
  getTokenExpiration,
  getTimeUntilExpiration,
  extractRealmRoles,
  hasRole,
  canAccess,
  mapLegacyPermissions,
  debugToken,
};
